import type { AxiosError } from 'axios';
import axios from 'axios';
import z from 'zod/v4';

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { OrgSchema } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { type FilteredInfo, type RequestParams, SortedInfo, type TableChange } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

const SupportResourceSchema = z.object({
  total_count: z.number(),
  id: z.uuidv4(),
  name: z.string(),
  phone_number: z.string().nullable(),
  description: z.string(),
  website: z.url().nullable(),
  subjects: z.array(z.string()),
  org_codes: z.array(OrgSchema).nullable(),
});

export type SupportResource = z.infer<typeof SupportResourceSchema>;

export type SupportResourceToUpsert = {
  id?: string;
  name: string;
  phone_number?: string;
  description: string;
  website?: string;
  subjects: string[];
  org_codes?: string[];
};

type AdminAdvocateResourcesSliceType = {
  resources: SupportResource[];
  loading: boolean;
  requestAdminAdvocateResourceParam: RequestParams<SupportResource>;
  resourceToUpsert: SupportResourceToUpsert | undefined;
  showEditResource: boolean;
  allUniqueSubjects: string[];
};

const initialState: AdminAdvocateResourcesSliceType = {
  resources: [],
  loading: false,
  requestAdminAdvocateResourceParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<SupportResource>('name', 'ascend'),
    filteredInfo: {},
  },
  resourceToUpsert: undefined,
  showEditResource: false,
  allUniqueSubjects: [],
};

export const fetchAdvocateResources = createAsyncThunk<SupportResource[], undefined, AsyncThunkConfig>(
  'adminAdvocateResources/fetchAdvocateResources',
  async (_, thunkAPI) => {
    try {
      const body = thunkAPI.getState().adminAdvocateResourcesSlice.requestAdminAdvocateResourceParam;
      const response = (await axios.post('/v0_advocate_support_resources', body)) as SupportResource[];
      const result = z.array(SupportResourceSchema).safeParse(response);
      if (!result.success) {
        logError(result.error, ['adminAdvocateResourcesSlice', 'fetchAdvocateResources', 'parsing']);
      }
      return response ?? [];
    } catch (e) {
      logError(e, ['adminAdvocateResourcesSlice', 'fetchAdvocateResources']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const upsertAdvocateResource = createAsyncThunk<boolean, SupportResourceToUpsert, AsyncThunkConfig>(
  'adminAdvocateResources/upsertAdvocateResource',
  async (resource, thunkAPI) => {
    try {
      const response = (await axios.post('/v0_upsert_advocate_support_resource', resource)) as boolean;
      return response ?? false;
    } catch (e) {
      logError(e, ['adminAdvocateResourcesSlice', 'upsertAdvocateResource']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const deleteAdvocateResource = createAsyncThunk<boolean, string, AsyncThunkConfig>(
  'adminAdvocateResources/deleteAdvocateResource',
  async (id, thunkAPI) => {
    try {
      const response = (await axios.post('/v0_delete_advocate_support_resource', { id })) as boolean;
      return response ?? false;
    } catch (e) {
      logError(e, ['adminAdvocateResourcesSlice', 'deleteAdvocateResource']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const adminAdvocateResourcesSlice = createSlice({
  name: 'adminAdvocateResources',
  initialState,
  reducers: {
    handleManagerTableChange: (state, action: PayloadAction<TableChange<SupportResource>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<SupportResource>;
      state.requestAdminAdvocateResourceParam.pagination = action.payload.pagination;
      state.requestAdminAdvocateResourceParam.sortedInfo = sorter;
      state.requestAdminAdvocateResourceParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestAdminAdvocateResourceParam.pagination = {
        current: 1,
        pageSize: 50,
        defaultPageSize: 50,
      };
      state.requestAdminAdvocateResourceParam.sortedInfo = new SortedInfo<SupportResource>('name', 'ascend');
      state.requestAdminAdvocateResourceParam.filteredInfo = {};
    },
    setAdvocateResource: (state, action: PayloadAction<SupportResourceToUpsert | undefined>) => {
      state.resourceToUpsert = action.payload;
    },
    setShowEditResource: (state, action: PayloadAction<boolean>) => {
      state.showEditResource = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdvocateResources.pending, (state, _action) => {
        state.loading = true;
      })
      .addCase(fetchAdvocateResources.fulfilled, (state, action) => {
        state.resources = action.payload;
        state.allUniqueSubjects = [...new Set(action.payload.flatMap((r) => r.subjects)).values()].sort();
        state.loading = false;
      })
      .addCase(fetchAdvocateResources.rejected, (state, _action) => {
        state.loading = false;
      });
  },
});

export const { handleManagerTableChange, clearFilter, setAdvocateResource, setShowEditResource } =
  adminAdvocateResourcesSlice.actions;
